<!-- src/views/pages/finance-accounts/sales/estimates-table.vue -->
<script setup>
const props = defineProps({
  rows: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(["edit", "delete", "preview", "convert"]);

const columns = [
  { sorter: false },
  {
    title: "Estimate",
    dataIndex: "estimate_no",
    key: "estimate_no",
    sorter: true,
  },
  {
    title: "Client Name",
    dataIndex: "client",
    key: "client",
    sorter: {
      compare: (a, b) => {
        const an = (a.client?.name || "").toLowerCase();
        const bn = (b.client?.name || "").toLowerCase();
        return an > bn ? -1 : bn > an ? 1 : 0;
      },
    },
  },
  {
    title: "Estimate Date",
    dataIndex: "estimate_date",
    key: "estimate_date",
    sorter: true,
  },
  {
    title: "Expiry Date",
    dataIndex: "expiry_date",
    key: "expiry_date",
    sorter: true,
  },
  {
    title: "Amount",
    dataIndex: "grand_total",
    key: "grand_total",
    sorter: true,
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    sorter: true,
  },
  {
    title: "",
    key: "action",
    sorter: false,
  },
];

const rowSelection = {
  onChange: () => {},
  onSelect: () => {},
  onSelectAll: () => {},
};

function statusClass(status) {
  switch ((status || "").toLowerCase()) {
    case "accepted":
      return "badge-soft-success";
    case "sent":
      return "badge-soft-purple";
    case "expired":
      return "badge-soft-warning";
    case "declined":
      return "badge-soft-danger";
    default:
      return "badge-soft-secondary";
  }
}

function formatStatus(status) {
  if (!status) return "";
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function formatMoney(val) {
  const num = Number(val || 0);
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
</script>

<template>
  <a-table
    class="table datatable thead-light"
    :columns="columns"
    :data-source="rows"
    :loading="loading"
    :row-selection="rowSelection"
    row-key="id"
  >
    <template #bodyCell="{ column, record }">
      <!-- Estimate No -->
      <template v-if="column.key === 'estimate_no'">
        <span class="fw-medium">{{ record.estimate_no }}</span>
      </template>

      <!-- Client -->
      <template v-else-if="column.key === 'client'">
        <div class="d-flex align-items-center file-name-icon">
          <a href="javascript:void(0);" class="avatar avatar-md avatar-rounded">
            <img
              v-if="record.client?.avatar_url"
              :src="record.client.avatar_url"
              class="img-fluid"
              alt="img"
            />
            <span
              v-else
              class="avatar-title bg-soft-primary text-primary rounded-circle"
            >
              {{ (record.client?.name || "?").charAt(0).toUpperCase() }}
            </span>
          </a>
          <div class="ms-2">
            <h6 class="fw-medium mb-0">
              <a href="javascript:void(0);">{{ record.client?.name }}</a>
            </h6>
            <span class="d-block mt-1">
              {{ record.client?.company_name || record.client?.email }}
            </span>
          </div>
        </div>
      </template>

      <!-- Amount -->
      <template v-else-if="column.key === 'grand_total'">
        {{ formatMoney(record.grand_total) }}
      </template>

      <!-- Status -->
      <template v-else-if="column.key === 'status'">
        <span class="badge" :class="statusClass(record.status)">
          {{ formatStatus(record.status) }}
        </span>
      </template>

      <!-- Actions -->
      <template v-else-if="column.key === 'action'">
        <div class="action-icon d-inline-flex">
          <!-- Preview -->
          <a
            href="javascript:void(0);"
            class="me-2"
            @click.prevent="emit('preview', record)"
            v-can="'Estimate.Read'"
          >
            <i class="ti ti-eye"></i>
          </a>
          <!-- Edit -->
          <a
            href="javascript:void(0);"
            class="me-2"
            @click.prevent="emit('edit', record)"
            v-can="'Estimate.Edit'"
          >
            <i class="ti ti-edit"></i>
          </a>
          <!-- Convert to Invoice -->
          <a
            v-if="(record.status || '').toLowerCase() === 'accepted'"
            href="javascript:void(0);"
            class="me-2"
            data-bs-toggle="modal"
            data-bs-target="#convert_estimate_modal"
            @click.prevent="emit('convert', record)"
            v-can="'Estimate.Edit'"
          >
            <i class="ti ti-file-invoice"></i>
          </a>
          <!-- Delete -->
          <a href="javascript:void(0);" @click.prevent="emit('delete', record)" v-can="'Estimate.Delete'">
            <i class="ti ti-trash"></i>
          </a>
        </div>
      </template>
    </template>
  </a-table>
</template>
